import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "../utils/cn";

export type DropdownItem = {
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  separator?: boolean; // If true, renders a separator line before this item
  sectionHeader?: boolean; // If true, renders as a section header (non-clickable, styled differently)
};

type DropdownProps = {
  label: string;
  items: DropdownItem[];
  align?: "left" | "right";
  state?: "default" | "hover" | "active" | "focus" | "disabled" | "loading";
};

export function Dropdown({ label, items, align = "left", state = "default" }: DropdownProps) {
  const dropdownId = useId();
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number; width: number; placement: "down" | "up" }>({
    top: 0,
    left: 0,
    width: 0,
    placement: "down",
  });
  const isDisabled = state === "disabled" || state === "loading";
  const stateClass =
    state === "hover"
      ? "bg-surface-muted"
      : state === "active"
        ? "bg-border/40"
        : state === "focus"
          ? "outline outline-2 outline-primary/40 outline-offset-2"
          : "";

  useEffect(() => {
    const handleGlobalOpen = (event: Event) => {
      const customEvent = event as CustomEvent<string>;
      if (customEvent.detail !== dropdownId) {
        setOpen(false);
      }
    };
    document.addEventListener("xlflow-dropdown-open", handleGlobalOpen as EventListener);
    return () => {
      document.removeEventListener("xlflow-dropdown-open", handleGlobalOpen as EventListener);
    };
  }, [dropdownId]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };
    const onMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (menuRef.current?.contains(target) || triggerRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onMouseDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onMouseDown);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open) return;
    const trigger = triggerRef.current;
    const menu = menuRef.current;
    if (!trigger || !menu) return;

    const rect = trigger.getBoundingClientRect();
    const menuRect = menu.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const shouldOpenUp = spaceBelow < menuRect.height && spaceAbove > menuRect.height;
    const top = shouldOpenUp ? rect.top - menuRect.height - 6 : rect.bottom + 6;

    const desiredLeft = align === "right" ? rect.right - menuRect.width : rect.left;
    const left = Math.max(8, Math.min(desiredLeft, window.innerWidth - menuRect.width - 8));

    setPosition({
      top,
      left,
      width: rect.width,
      placement: shouldOpenUp ? "up" : "down",
    });
  }, [open, align]);

  useEffect(() => {
    if (!open) return;
    const onResize = () => setOpen(false);
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
  }, [open]);
  return (
    <div className="relative inline-flex">
      <button
        ref={triggerRef}
        className={cn(
          "rounded-md border border-border bg-surface px-3 py-1 text-sm text-text transition duration-150 ease-out hover:-translate-y-0.5",
          stateClass
        )}
        type="button"
        onClick={() => {
          if (isDisabled) return;
          const next = !open;
          if (next) {
            document.dispatchEvent(new CustomEvent("xlflow-dropdown-open", { detail: dropdownId }));
          }
          setOpen(next);
        }}
        disabled={isDisabled}
      >
        {state === "loading" ? "Chargement…" : label}
      </button>
      {open
        ? createPortal(
            <div
              ref={menuRef}
              className="dropdown-panel fixed z-50 w-48 rounded-md border border-border bg-surface shadow-sm"
              style={{ top: position.top, left: position.left }}
              data-placement={position.placement}
            >
              {items.map((item, index) => (
                <div key={item.label || `separator-${index}`}>
                  {item.separator && index > 0 && (
                    <div className="border-t border-border my-1" />
                  )}
                  {item.label ? (
                    item.sectionHeader ? (
                      <div className="px-3 py-2 text-xs font-semibold text-muted uppercase tracking-wider">
                        {item.label}
                      </div>
                    ) : (
                      <button
                        type="button"
                        className={cn(
                          "block w-full px-3 py-2 text-left text-sm text-text hover:bg-surface-muted",
                          item.disabled && "opacity-50 pointer-events-none"
                        )}
                        onClick={() => {
                          if (item.disabled) return;
                          item.onClick?.();
                          setOpen(false);
                        }}
                      >
                        {item.label}
                      </button>
                    )
                  ) : null}
                </div>
              ))}
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
